import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { Sun, Moon, Monitor } from 'lucide-react';

/**
 * ThemeSwitcher
 *
 * Minimal, high-precision segmented control for switching between
 * System, Dark, and Light themes.
 * Adheres to the "Silence & Warm Gold" aesthetic.
 */
export default function ThemeSwitcher({ className = '' }) {
  const { mode, setMode } = useTheme();

  const options = [
    { id: 'system', label: 'Auto', icon: Monitor },
    { id: 'light',  label: 'Light', icon: Sun },
    { id: 'dark',   label: 'Dark',  icon: Moon },
  ];

  return (
    <div className={`flex flex-col gap-1.5 px-1 py-2 w-full ${className}`}>
      <span
        className="text-[10px] font-mono font-bold tracking-wider uppercase select-none opacity-80 px-1"
        style={{ color: 'var(--text-faint)' }}
      >
        Appearance
      </span>
      <div
        className="flex w-full p-0.5 card-border-subtle transition-all"
        style={{
          borderRadius: 'var(--radius-pill)',
          backgroundColor: 'var(--surface-2)',
          borderColor: 'var(--border-subtle)',
        }}
        role="group"
        aria-label="Theme mode switcher"
      >
        {options.map((opt) => {
          const isActive = mode === opt.id;
          const Icon = opt.icon;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => setMode(opt.id)}
              aria-pressed={isActive}
              title={`Switch to ${opt.label} mode`}
              className={`flex-1 min-w-0 flex items-center justify-center gap-1 py-1.5 px-1.5 text-xs font-semibold whitespace-nowrap transition-all duration-150 cursor-pointer ${
                isActive
                  ? 'shadow-xs'
                  : 'hover:opacity-90'
              }`}
              style={{
                borderRadius: 'var(--radius-pill)',
                backgroundColor: isActive ? 'var(--accent-soft)' : 'transparent',
                color: isActive ? 'var(--accent)' : 'var(--text-muted)',
                border: isActive ? '1px solid var(--border-active)' : '1px solid transparent',
              }}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
